import { useQuery, useQueryClient } from 'react-query';

type Holds = Record<string, symbol>;

/** Session-scoped retry guard, never an identity or permission assertion. */
export function useStaffReconciliation(
  sessionKey: string,
  readStaff: () => Promise<{ personId: string; tier?: string | null }[]>
) {
  const client = useQueryClient();
  const key = ['staffUnconfirmedTargets', sessionKey];
  // Survives page unmount/GC while unresolved; not persisted to browser storage.
  const { data: holds = {} } = useQuery<Holds>(key, { enabled: false, initialData: {}, cacheTime: Infinity });
  const isBlocked = (id: string) => Boolean(client.getQueryData<Holds>(key)?.[id]);
  const verify = async (id: string) => {
    const attempt = client.getQueryData<Holds>(key)?.[id];
    if (!attempt) return;
    try {
      // Cancel/discard an older read before demanding a fresh network read.
      await client.cancelQueries('staff');
      const rows = await client.fetchQuery('staff', readStaff, { staleTime: 0, retry: false });
      const target = rows.find((row) => row.personId === id);
      if (target && ['none', 'staff', 'admin'].includes(target.tier ?? '')) {
        client.setQueryData<Holds>(key, (current = {}) => {
          if (current[id] !== attempt) return current;
          const next = { ...current };
          delete next[id];
          return next;
        });
      }
    } catch {
      // Failed/absent/older/cached reads never verify an unknown write outcome.
    }
  };
  const hold = (id: string) => {
    client.setQueryData<Holds>(key, (current = {}) => ({ ...current, [id]: Symbol('unconfirmed') }));
    void verify(id);
  };
  return { blocked: Object.keys(holds), isBlocked, hold, verify };
}
